// ===========================================
// Create Meet & Greet Dialog (admin)
// ===========================================
// Standalone dialog mirroring the public M&G form (student details, contacts,
// emergency, instrument, notes) PLUS an optional date/time and teacher, and HOW FAR
// the booking goes (#425): Initial form (optionally emailing the verification link),
// Verified, or Approved (the student is joining: emails the registration link, as
// Approve does). Used by the Calendar toolbar and the Meet & Greet page header.

import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTeachers } from '../../hooks/useUsers';
import { useInstruments } from '../../hooks/useAdmin';
import { useAdminCreateMeetAndGreet } from '../../hooks/useMeetAndGreet';
import { Teacher } from '../../api/users.api';
import { Instrument } from '../../api/admin.api';
import { AdminCreateMeetAndGreetInput, AdminCreateStage } from '../../api/meetAndGreet.api';

// ===========================================
// FORM STATE
// ===========================================

interface FormState {
  studentFirstName: string;
  studentLastName: string;
  studentAge: string; // string for TextField; coerced on submit
  contact1Name: string;
  contact1Email: string;
  contact1Phone: string;
  contact1Relationship: string;
  contact2Name: string;
  contact2Email: string;
  contact2Phone: string;
  contact2Relationship: string;
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelationship: string;
  instrumentId: string;
  additionalNotes: string;
  scheduledDateTime: string; // datetime-local "YYYY-MM-DDTHH:mm"; optional (#425)
  assignedTeacherId: string;
  // How far the booking goes (#425). In the form state so Cancel resets it (/qa M3).
  stage: AdminCreateStage;
  sendVerificationEmail: boolean;
}

const emptyForm: FormState = {
  studentFirstName: '',
  studentLastName: '',
  studentAge: '',
  contact1Name: '',
  contact1Email: '',
  contact1Phone: '',
  contact1Relationship: '',
  contact2Name: '',
  contact2Email: '',
  contact2Phone: '',
  contact2Relationship: '',
  emergencyName: '',
  emergencyPhone: '',
  emergencyRelationship: '',
  instrumentId: '',
  additionalNotes: '',
  scheduledDateTime: '',
  assignedTeacherId: '',
  stage: 'FORM', // default: don’t assume (Andrew 12/10)
  sendVerificationEmail: false,
};

// #425: the three stages, each with what it does. 'Approved' means the student is
// joining the school (decided after the Meet & Greet), so it sends the link.
const STAGE_OPTIONS: { value: AdminCreateStage; label: string; help: string }[] = [
  {
    value: 'FORM',
    label: 'Initial form',
    help: 'Saves the booking. The family confirms their email before you approve.',
  },
  {
    value: 'VERIFIED',
    label: 'Verified',
    help: 'You have confirmed the family’s details. The booking waits for the Meet & Greet, then Approve, Waitlist or Reject.',
  },
  {
    value: 'APPROVED',
    label: 'Approved: the student is joining',
    help: 'Emails the family the registration and payment link now, as Approve does.',
  },
];

const RELATIONSHIPS = ['Mother', 'Father', 'Guardian', 'Grandparent', 'Other'];

// ===========================================
// DIALOG
// ===========================================

export interface CreateMeetAndGreetDialogPrefill {
  date?: Date;
}

export interface CreateMeetAndGreetDialogProps {
  open: boolean;
  onClose: () => void;
  prefill?: CreateMeetAndGreetDialogPrefill;
}

function toDateTimeLocal(date: Date): string {
  // datetime-local expects YYYY-MM-DDTHH:mm in the browser's local tz
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function CreateMeetAndGreetDialog({
  open,
  onClose,
  prefill,
}: CreateMeetAndGreetDialogProps) {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: teachers = [] } = useTeachers({ enabled: open });
  const { data: instruments = [] } = useInstruments();
  const createMutation = useAdminCreateMeetAndGreet();

  // Apply prefill when dialog opens with a date
  useEffect(() => {
    if (!open || !prefill?.date) return;
    setForm((prev) => ({
      ...prev,
      scheduledDateTime: prev.scheduledDateTime || toDateTimeLocal(prefill.date!),
    }));
  }, [open, prefill?.date]);

  const update = (patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  };

  const handleClose = () => {
    setForm(emptyForm);
    setErrors({});
    onClose();
  };

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.studentFirstName.trim()) e.studentFirstName = 'Required';
    if (!form.studentLastName.trim()) e.studentLastName = 'Required';
    const age = parseInt(form.studentAge, 10);
    if (Number.isNaN(age) || age < 3 || age > 99) e.studentAge = 'Age 3–99';
    if (!form.contact1Name.trim()) e.contact1Name = 'Required';
    if (!form.contact1Email.trim()) e.contact1Email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact1Email))
      e.contact1Email = 'Invalid email';
    if (!form.contact1Phone.trim()) e.contact1Phone = 'Required';
    if (!form.contact1Relationship.trim()) e.contact1Relationship = 'Required';
    if (!form.emergencyName.trim()) e.emergencyName = 'Required';
    if (!form.emergencyPhone.trim()) e.emergencyPhone = 'Required';
    if (!form.emergencyRelationship.trim()) e.emergencyRelationship = 'Required';
    return e;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload: AdminCreateMeetAndGreetInput = {
      studentFirstName: form.studentFirstName.trim(),
      studentLastName: form.studentLastName.trim(),
      studentAge: parseInt(form.studentAge, 10),
      contact1Name: form.contact1Name.trim(),
      contact1Email: form.contact1Email.trim(),
      contact1Phone: form.contact1Phone.trim(),
      contact1Relationship: form.contact1Relationship.trim(),
      contact2Name: form.contact2Name.trim() || null,
      contact2Email: form.contact2Email.trim() || null,
      contact2Phone: form.contact2Phone.trim() || null,
      contact2Relationship: form.contact2Relationship.trim() || null,
      emergencyName: form.emergencyName.trim(),
      emergencyPhone: form.emergencyPhone.trim(),
      emergencyRelationship: form.emergencyRelationship.trim(),
      instrumentId: form.instrumentId || null,
      additionalNotes: form.additionalNotes.trim() || null,
      scheduledDateTime: form.scheduledDateTime
        ? new Date(form.scheduledDateTime).toISOString()
        : null,
      assignedTeacherId: form.assignedTeacherId || null,
      stage: form.stage,
      // Only FORM sends it; a toggle left on under another stage is dropped (/qa M3).
      sendVerificationEmail: form.stage === 'FORM' && form.sendVerificationEmail,
    };

    createMutation.mutate(payload, { onSuccess: handleClose });
  };

  const selectedInstrument =
    instruments.find((i: Instrument) => i.id === form.instrumentId) || null;
  const selectedTeacher =
    teachers.find((t: Teacher) => t.id === form.assignedTeacherId) || null;

  const disabled = createMutation.isPending;
  const familyEmail = form.contact1Email.trim() || 'the family';
  const submitLabel =
    form.stage === 'APPROVED'
      ? 'Create & send registration link'
      : form.stage === 'FORM' && form.sendVerificationEmail
        ? 'Create & send verification email'
        : 'Create Meet & Greet';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Meet & Greet</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 3 }}>
          {/* Student details */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Student Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="First Name"
                  value={form.studentFirstName}
                  onChange={(e) => update({ studentFirstName: e.target.value })}
                  error={!!errors.studentFirstName}
                  helperText={errors.studentFirstName}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Last Name"
                  value={form.studentLastName}
                  onChange={(e) => update({ studentLastName: e.target.value })}
                  error={!!errors.studentLastName}
                  helperText={errors.studentLastName}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="Age"
                  value={form.studentAge}
                  onChange={(e) => update({ studentAge: e.target.value })}
                  inputProps={{ min: 3, max: 99 }}
                  error={!!errors.studentAge}
                  helperText={errors.studentAge}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Primary contact */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Primary Contact
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Name"
                  value={form.contact1Name}
                  onChange={(e) => update({ contact1Name: e.target.value })}
                  error={!!errors.contact1Name}
                  helperText={errors.contact1Name}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={RELATIONSHIPS}
                  value={form.contact1Relationship}
                  onChange={(_, v) => update({ contact1Relationship: v ?? '' })}
                  onInputChange={(_, v) => update({ contact1Relationship: v })}
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Relationship"
                      required
                      error={!!errors.contact1Relationship}
                      helperText={errors.contact1Relationship}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="email"
                  label="Email"
                  value={form.contact1Email}
                  onChange={(e) => update({ contact1Email: e.target.value })}
                  error={!!errors.contact1Email}
                  helperText={errors.contact1Email}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Phone"
                  value={form.contact1Phone}
                  onChange={(e) => update({ contact1Phone: e.target.value })}
                  error={!!errors.contact1Phone}
                  helperText={errors.contact1Phone}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Secondary contact (optional) */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Secondary Contact{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                (optional)
              </Typography>
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name"
                  value={form.contact2Name}
                  onChange={(e) => update({ contact2Name: e.target.value })}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={RELATIONSHIPS}
                  value={form.contact2Relationship}
                  onChange={(_, v) => update({ contact2Relationship: v ?? '' })}
                  onInputChange={(_, v) => update({ contact2Relationship: v })}
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Relationship" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="Email"
                  value={form.contact2Email}
                  onChange={(e) => update({ contact2Email: e.target.value })}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone"
                  value={form.contact2Phone}
                  onChange={(e) => update({ contact2Phone: e.target.value })}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Emergency contact */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Emergency Contact
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Name"
                  value={form.emergencyName}
                  onChange={(e) => update({ emergencyName: e.target.value })}
                  error={!!errors.emergencyName}
                  helperText={errors.emergencyName}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Phone"
                  value={form.emergencyPhone}
                  onChange={(e) => update({ emergencyPhone: e.target.value })}
                  error={!!errors.emergencyPhone}
                  helperText={errors.emergencyPhone}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  options={RELATIONSHIPS}
                  value={form.emergencyRelationship}
                  onChange={(_, v) => update({ emergencyRelationship: v ?? '' })}
                  onInputChange={(_, v) => update({ emergencyRelationship: v })}
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Relationship"
                      required
                      error={!!errors.emergencyRelationship}
                      helperText={errors.emergencyRelationship}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Preferences + Schedule */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Schedule & Preferences
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="Date & Time (optional)"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  value={form.scheduledDateTime}
                  onChange={(e) => update({ scheduledDateTime: e.target.value })}
                  error={!!errors.scheduledDateTime}
                  helperText={errors.scheduledDateTime}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={teachers}
                  getOptionLabel={(t: Teacher) =>
                    `${t.User.firstName} ${t.User.lastName}`
                  }
                  value={selectedTeacher}
                  onChange={(_, t) =>
                    update({ assignedTeacherId: t?.id || '' })
                  }
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Assigned Teacher (optional)"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={instruments.filter((i: Instrument) => i.isActive)}
                  getOptionLabel={(i: Instrument) => i.name}
                  value={selectedInstrument}
                  onChange={(_, i) => update({ instrumentId: i?.id || '' })}
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Instrument (optional)"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  label="Additional Notes (optional)"
                  value={form.additionalNotes}
                  onChange={(e) => update({ additionalNotes: e.target.value })}
                  inputProps={{ maxLength: 500 }}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* How far it goes (#425) */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }} id="mag-stage-label">
              How far does this booking go?
            </Typography>
            <RadioGroup
              aria-labelledby="mag-stage-label"
              value={form.stage}
              onChange={(e) => update({ stage: e.target.value as AdminCreateStage })}
            >
              {STAGE_OPTIONS.map((opt) => (
                <Box key={opt.value} sx={{ mb: 1 }}>
                  <FormControlLabel
                    value={opt.value}
                    control={<Radio size="small" />}
                    label={opt.label}
                    disabled={disabled}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
                    {opt.help}
                  </Typography>
                  {opt.value === 'FORM' && form.stage === 'FORM' && (
                    <Box sx={{ pl: 4, mt: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={form.sendVerificationEmail}
                            onChange={(e) => update({ sendVerificationEmail: e.target.checked })}
                          />
                        }
                        label={`Send the verification email to ${familyEmail}`}
                        disabled={disabled}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </RadioGroup>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={disabled}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={disabled}>
          {disabled ? 'Creating...' : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
